import { useState, useEffect, useRef, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { 
  LayoutDashboard, Clock, Users, Upload, FileText, 
  Settings, LogOut, Menu, X, Bell, BellOff, Volume2, VolumeX, BellRing, Key, CreditCard, UserCog, Wallet, Send, Eye, Database, BarChart3, MapPin, Globe2, KeyRound, HardDrive, CheckSquare, Mail, Sparkles
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import InstallPWAAdmin from "@/components/InstallPWAAdmin";
import { ThemeToggle } from "@/components/ThemeToggle";
import { useTheme } from "@/context/ThemeContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const navItems = [
  { path: "/admin/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { path: "/admin/analytics", label: "Analytics", icon: BarChart3 },
  { path: "/admin/ai-assistant", label: "Asisten AI (ChatGPT)", icon: Sparkles },
  { path: "/admin/pending", label: "Pembayaran Pending", icon: Clock },
  { path: "/admin/topup-requests", label: "Permintaan Top-Up", icon: Wallet },
  { path: "/admin/gmail", label: "Bot Pembayaran (Gmail)", icon: Mail },
  { path: "/admin/installment", label: "Kode Cicilan", icon: Key },
  { path: "/admin/customers", label: "Pelanggan", icon: Users },
  { path: "/admin/customer-locations", label: "Lokasi Pelanggan", icon: MapPin },
  { path: "/admin/master-data", label: "Master Data (SHP)", icon: Database },
  { path: "/peta-global", label: "Peta Global", icon: Globe2 },
  { path: "/admin/peta-code", label: "Kode Update Peta", icon: KeyRound },
  { path: "/admin/users", label: "Kelola Petugas/Agen", icon: UserCog },
  { path: "/admin/pengawas", label: "Kelola Pengawas", icon: Eye },
  { path: "/admin/upload", label: "Upload PDF", icon: Upload },
  { path: "/admin/reports", label: "Laporan", icon: FileText },
  { path: "/admin/transactions", label: "Kelola Transaksi", icon: CreditCard },
  { path: "/admin/rekonsiliasi", label: "Rekonsiliasi Setoran", icon: CheckSquare },
  { path: "/admin/notifications", label: "Kirim Notifikasi", icon: Send },
  { path: "/admin/settings", label: "Pengaturan", icon: Settings },
  { path: "/admin/database", label: "Database Monitor", icon: Database },
  { path: "/admin/backup", label: "Backup & Restore", icon: HardDrive },
];

// Helper function to convert URL-safe base64 to Uint8Array
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

// Notification sound - using a simple beep
const playNotificationSound = () => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    
    // Create a more pleasant notification sound
    const playTone = (freq, startTime, duration) => {
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = freq;
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(0, startTime);
      gainNode.gain.linearRampToValueAtTime(0.3, startTime + 0.05);
      gainNode.gain.linearRampToValueAtTime(0, startTime + duration);
      
      oscillator.start(startTime);
      oscillator.stop(startTime + duration);
    };
    
    // Play a pleasant two-tone notification
    const now = audioContext.currentTime;
    playTone(880, now, 0.15);        // A5
    playTone(1108.73, now + 0.15, 0.2); // C#6
    playTone(1318.51, now + 0.35, 0.25); // E6
    
  } catch (e) {
    console.log("Could not play notification sound:", e);
  }
};

export const AdminLayout = ({ children, title }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(() => {
    return localStorage.getItem("admin_sound_enabled") !== "false";
  });
  const [notificationPermission, setNotificationPermission] = useState("default");
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [newPaymentAlert, setNewPaymentAlert] = useState(false);
  const [unreadNotifCount, setUnreadNotifCount] = useState(0);
  const lastPendingCountRef = useRef(0);
  const adminName = localStorage.getItem("admin_name") || "Admin";

  // Subscribe to push notifications
  const subscribeToPush = async () => {
    try {
      if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
        toast.error("Browser tidak mendukung Push Notification");
        return;
      }

      // Request notification permission first
      const permission = await Notification.requestPermission();
      setNotificationPermission(permission);
      
      if (permission !== 'granted') {
        toast.error("Izin notifikasi ditolak. Aktifkan di pengaturan browser.");
        return;
      }

      // Get VAPID public key from server
      const { data: vapidData } = await axios.get(`${API}/admin/vapid-public-key`);
      const vapidPublicKey = vapidData.publicKey;

      if (!vapidPublicKey) {
        toast.error("Push notification belum dikonfigurasi di server");
        return;
      }

      // Register service worker if not already
      const registration = await navigator.serviceWorker.ready;

      // Subscribe to push
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
      });

      // Send subscription to server
      const token = localStorage.getItem("admin_token");
      await axios.post(`${API}/admin/push-subscribe`, 
        {
          endpoint: subscription.endpoint,
          keys: {
            p256dh: btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('p256dh')))),
            auth: btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('auth'))))
          }
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setPushSubscribed(true);
      localStorage.setItem("push_subscribed", "true");
      toast.success("🔔 Push Notification aktif! Anda akan menerima notifikasi bahkan saat browser tertutup.");

      // Send test notification
      await axios.post(`${API}/admin/push-test`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

    } catch (error) {
      console.error("Push subscription error:", error);
      toast.error("Gagal mengaktifkan push notification: " + (error.response?.data?.detail || error.message));
    }
  };

  // Check if already subscribed
  const checkPushSubscription = async () => {
    try {
      if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
      
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      
      if (subscription) {
        setPushSubscribed(true);
        localStorage.setItem("push_subscribed", "true");
      } else {
        setPushSubscribed(false);
        localStorage.removeItem("push_subscribed");
      }
    } catch (e) {
      console.log("Error checking push subscription:", e);
    }
  };

  // Legacy notification permission request (for browser notifications when app is open)
  const requestNotificationPermission = async () => {
    // Use the new push subscription flow
    await subscribeToPush();
  };

  // Show browser notification
  const showBrowserNotification = (title, body, data = {}) => {
    if (Notification.permission !== "granted") return;

    try {
      const notification = new Notification(title, {
        body: body,
        icon: "/icons/icon-192x192.png",
        badge: "/icons/icon-72x72.png",
        tag: "payment-notification",
        requireInteraction: true, // Keep notification until user interacts
        vibrate: [200, 100, 200], // Vibration pattern
        data: data
      });

      notification.onclick = () => {
        window.focus();
        navigate("/admin/pending");
        notification.close();
      };
    } catch (error) {
      console.error("Error showing notification:", error);
    }
  };

  // Check notification permission and push subscription on load
  useEffect(() => {
    if ("Notification" in window) {
      setNotificationPermission(Notification.permission);
    }
    checkPushSubscription();
  }, []);

  // Toggle sound
  const toggleSound = () => {
    const newValue = !soundEnabled;
    setSoundEnabled(newValue);
    localStorage.setItem("admin_sound_enabled", newValue.toString());
    toast.success(newValue ? "Notifikasi suara diaktifkan" : "Notifikasi suara dinonaktifkan");
  };

  // Check for new pending payments
  const checkNewPayments = useCallback(async () => {
    try {
      const token = localStorage.getItem("admin_token");
      if (!token) return;

      const response = await axios.get(`${API}/admin/payments/pending`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const currentCount = response.data.length;
      const payments = response.data;
      
      // If there are more pending payments than before, notify!
      if (currentCount > lastPendingCountRef.current && lastPendingCountRef.current !== 0) {
        const newPayments = currentCount - lastPendingCountRef.current;
        const latestPayment = payments[0]; // Most recent payment
        
        setNewPaymentAlert(true);
        setTimeout(() => setNewPaymentAlert(false), 5000);
        
        // Play sound if enabled
        if (soundEnabled) {
          playNotificationSound();
        }
        
        // Show browser push notification (works even when app is in background!)
        const notifBody = latestPayment 
          ? `${latestPayment.nama} - ${new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(latestPayment.total_bayar)}`
          : `${newPayments} pembayaran baru menunggu konfirmasi`;
        
        showBrowserNotification(
          `🔔 Pembayaran Baru!`,
          notifBody,
          { paymentId: latestPayment?.id }
        );
        
        // Show toast notification
        toast.success(
          `🔔 ${newPayments} Pembayaran Baru Masuk!`, 
          { 
            duration: 10000,
            action: {
              label: "Lihat",
              onClick: () => navigate("/admin/pending")
            }
          }
        );
      }
      
      lastPendingCountRef.current = currentCount;
      setPendingCount(currentCount);
      
    } catch (error) {
      console.error("Error checking payments:", error);
    }
  }, [soundEnabled, navigate]);

  // Check for unread admin notifications (for badge)
  const checkUnreadNotifications = useCallback(async () => {
    try {
      const token = localStorage.getItem("admin_token");
      if (!token) return;

      const response = await axios.get(`${API}/admin/notifications/unread-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setUnreadNotifCount(response.data.unread_count || 0);
    } catch (error) {
      console.error("Error checking unread notifications:", error);
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    if (!token) {
      navigate("/admin/login");
      return;
    }

    // Initial check
    checkNewPayments();
    checkUnreadNotifications();

    // Poll for new payments and notifications every 10 seconds
    const interval = setInterval(() => {
      checkNewPayments();
      checkUnreadNotifications();
    }, 10000);
    
    return () => clearInterval(interval);
  }, [navigate, checkNewPayments, checkUnreadNotifications]);

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_name");
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen bg-slate-100 dark:bg-slate-950 transition-colors duration-300">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 h-full w-64 bg-slate-900 dark:bg-slate-950 z-50 transform transition-transform duration-200
        ${sidebarOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0 border-r border-slate-800
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-slate-800">
            <div className="flex items-center gap-3">
              <img src="/icons/icon-72x72.png" alt="Logo" className="w-10 h-10 rounded-xl" />
              <div>
                <span className="text-lg font-heading font-semibold text-white">Produk Digital</span>
                <p className="text-xs text-slate-400">{adminName}</p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              const isPending = item.path === "/admin/pending";
              const isTopupRequests = item.path === "/admin/topup-requests";
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`nav-item ${isActive ? "active" : ""} ${isPending && newPaymentAlert ? "animate-pulse bg-emerald-900/30" : ""}`}
                  data-testid={`nav-${item.path.split("/").pop()}`}
                >
                  <Icon className="w-5 h-5" />
                  <span>{item.label}</span>
                  {isPending && pendingCount > 0 && (
                    <span className={`ml-auto px-2 py-0.5 text-xs font-bold rounded-full ${
                      newPaymentAlert 
                        ? "bg-emerald-500 text-white animate-bounce" 
                        : "bg-amber-500 text-white"
                    }`}>
                      {pendingCount}
                    </span>
                  )}
                  {isTopupRequests && unreadNotifCount > 0 && (
                    <span className="ml-auto px-2 py-0.5 text-xs font-bold rounded-full bg-red-500 text-white animate-pulse">
                      {unreadNotifCount > 9 ? "9+" : unreadNotifCount}
                    </span>
                  )}
                </Link>
              );
            })}
          </nav>

          {/* Logout */}
          <div className="p-4 border-t border-slate-800">
            <button
              onClick={handleLogout}
              className="nav-item w-full text-red-400 hover:text-red-300 hover:bg-red-900/20"
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
              <span>Keluar</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <header className="sticky top-0 z-30 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 px-4 py-3 lg:px-6 transition-colors duration-300">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800"
              >
                <Menu className="w-5 h-5 text-slate-600 dark:text-slate-300" />
              </button>
              <h1 className="text-lg font-heading font-semibold text-slate-800 dark:text-white">
                {title}
              </h1>
            </div>
            <div className="flex items-center gap-2">
              {/* Theme Toggle */}
              <ThemeToggle 
                variant="outline" 
                className="border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
              />
              
              {/* Push notification button */}
              {!pushSubscribed ? (
                <button
                  onClick={subscribeToPush}
                  className="p-2 rounded-lg bg-orange-100 text-orange-600 hover:bg-orange-200 transition-colors"
                  title="Aktifkan Push Notification"
                  data-testid="enable-push-btn"
                >
                  <BellOff className="w-5 h-5" />
                </button>
              ) : (
                <button
                  className="p-2 rounded-lg bg-emerald-100 text-emerald-600 cursor-default"
                  title="Push Notification aktif"
                  data-testid="push-active-btn"
                >
                  <BellRing className="w-5 h-5" />
                </button>
              )}
              
              {/* Sound toggle button */}
              <button
                onClick={toggleSound}
                className={`p-2 rounded-lg transition-colors ${
                  soundEnabled 
                    ? "bg-emerald-100 text-emerald-600 hover:bg-emerald-200" 
                    : "bg-slate-100 text-slate-400 hover:bg-slate-200"
                }`}
                title={soundEnabled ? "Matikan suara notifikasi" : "Aktifkan suara notifikasi"}
                data-testid="toggle-sound-btn"
              >
                {soundEnabled ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
              </button>
              
              {/* Pending notification badge */}
              {pendingCount > 0 && (
                <Link 
                  to="/admin/pending"
                  className={`relative p-2 rounded-lg ${
                    newPaymentAlert 
                      ? "bg-emerald-100 text-emerald-600 animate-pulse" 
                      : "bg-amber-100 text-amber-600"
                  } hover:opacity-80`}
                  title={`${pendingCount} pembayaran menunggu konfirmasi`}
                >
                  <Bell className="w-5 h-5" />
                  <span className={`absolute -top-1 -right-1 w-5 h-5 text-xs font-bold rounded-full flex items-center justify-center ${
                    newPaymentAlert ? "bg-emerald-500 text-white" : "bg-amber-500 text-white"
                  }`}>
                    {pendingCount > 9 ? "9+" : pendingCount}
                  </span>
                </Link>
              )}
              
              <Link to="/" target="_blank">
                <Button variant="outline" size="sm">
                  Lihat Website
                </Button>
              </Link>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 lg:p-6">
          <div className="page-transition">
            {children}
          </div>
        </main>
      </div>
      
      {/* PWA Install Prompt for Admin */}
      <InstallPWAAdmin />
    </div>
  );
};

export default AdminLayout;
